import { BUDGET_STATUS_LABELS, type Budget } from "@station/contracts";
import {
  Alert,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  Input,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FileChartColumn, Plus, Target } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { Amount, todayIstanbul } from "../../../features/accounting/shared.js";
import { useBudgetPermissions, useBudgets, useRefreshBudgets } from "../../../features/budget/queries.js";
import { BudgetStatusBadge } from "../../../features/budget/shared.js";
import { ExpensesPage } from "../../../features/expenses/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";

export const Route = createFileRoute("/_app/butce/")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: BudgetListPage,
});

function BudgetListPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useBudgetPermissions();
  const [creating, setCreating] = useState(false);

  return (
    <ExpensesPage
      title={t("menu.budget")}
      description={t("budget.description")}
      breadcrumb={[{ label: t("menu.budget") }]}
      actions={
        <>
          <Button variant="outline" asChild>
            <Link to="/butce/rapor">
              <FileChartColumn aria-hidden="true" />
              {t("budget.report.title")}
            </Link>
          </Button>
          {permissions.canManage ? (
            <Button onClick={() => setCreating(true)}>
              <Plus aria-hidden="true" />
              {t("budget.new")}
            </Button>
          ) : null}
        </>
      }
    >
      {({ companyId }) => (
        <>
          <BudgetTable companyId={companyId} />
          <CreateBudgetDialog companyId={companyId} open={creating} onOpenChange={setCreating} />
        </>
      )}
    </ExpensesPage>
  );
}

function BudgetTable({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshBudgets();
  const permissions = useBudgetPermissions();
  const budgets = useBudgets(companyId);
  const [activating, setActivating] = useState<Budget | null>(null);
  const [versioning, setVersioning] = useState<Budget | null>(null);

  const activate = useMutation({
    mutationFn: async (budget: Budget) =>
      api.POST("/budgets/{id}/activate", {
        params: { path: { id: budget.id } },
        body: { version: budget.version },
      }),
    onSuccess: async () => {
      setActivating(null);
      await refresh();
      toast({ tone: "success", title: t("budget.activated") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const createVersion = useMutation({
    mutationFn: async (budget: Budget) =>
      (
        await api.POST("/budgets/{id}/versions", {
          params: { path: { id: budget.id } },
          body: { version: budget.version },
        })
      ).data!,
    onSuccess: async () => {
      setVersioning(null);
      await refresh();
      toast({ tone: "success", title: t("budget.versionCreated") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const columns = useMemo<DataTableColumn<Budget>[]>(
    () => [
      {
        id: "name",
        header: t("budget.fields.name"),
        hideable: false,
        cell: (row) => (
          <Link to="/butce/$id" params={{ id: row.id }} className="font-medium underline underline-offset-4">
            {row.name}
          </Link>
        ),
        exportValue: (row) => row.name,
      },
      {
        id: "fiscalYear",
        header: t("budget.fields.fiscalYear"),
        align: "center",
        cell: (row) => String(row.fiscalYear),
        exportValue: (row) => row.fiscalYear,
      },
      {
        id: "versionNo",
        header: t("budget.fields.versionNo"),
        align: "center",
        cell: (row) => String(row.versionNo),
        exportValue: (row) => row.versionNo,
      },
      {
        id: "lineCount",
        header: t("budget.fields.lineCount"),
        align: "center",
        cell: (row) => String(row.lineCount),
        exportValue: (row) => row.lineCount,
      },
      {
        id: "totalAmount",
        header: t("budget.fields.totalAmount"),
        align: "right",
        cell: (row) => <Amount value={row.totalAmount} />,
        exportValue: (row) => row.totalAmount,
      },
      {
        id: "variancePercent",
        header: t("budget.fields.variancePercent"),
        align: "center",
        cell: (row) => `%${row.variancePercent.replace(".", ",")}`,
      },
      {
        id: "status",
        header: t("common.status"),
        hideable: false,
        cell: (row) => <BudgetStatusBadge status={row.status} />,
        exportValue: (row) => BUDGET_STATUS_LABELS[row.status],
      },
      {
        id: "actions",
        header: t("common.actions"),
        align: "right",
        cell: (row) =>
          permissions.canManage ? (
            <span className="flex justify-end gap-1">
              {row.status === "DRAFT" ? (
                <Button variant="ghost" size="sm" onClick={() => setActivating(row)}>
                  {t("budget.activate")}
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setVersioning(row)}>
                  {t("budget.newVersion")}
                </Button>
              )}
            </span>
          ) : null,
      },
    ],
    [t, permissions.canManage],
  );

  return (
    <>
      <DataTable
        caption={t("menu.budget")}
        columns={columns}
        rows={budgets.data ?? []}
        rowKey={(row) => row.id}
        loading={budgets.isPending}
        error={budgets.error ? <Alert tone="danger">{errorMessage(budgets.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Target />}
            title={t("budget.emptyTitle")}
            description={t("budget.emptyDescription")}
          />
        }
      />

      <ConfirmDialog
        open={activating !== null}
        onOpenChange={(open) => {
          if (!open) setActivating(null);
        }}
        title={t("budget.activateTitle")}
        description={t("budget.activateDescription", { year: activating?.fiscalYear ?? "" })}
        confirmLabel={t("budget.activate")}
        onConfirm={() => {
          if (activating) activate.mutate(activating);
        }}
      />

      <ConfirmDialog
        open={versioning !== null}
        onOpenChange={(open) => {
          if (!open) setVersioning(null);
        }}
        title={t("budget.newVersionTitle")}
        description={t("budget.newVersionDescription")}
        confirmLabel={t("budget.newVersion")}
        onConfirm={() => {
          if (versioning) createVersion.mutate(versioning);
        }}
      />
    </>
  );
}

function CreateBudgetDialog({
  companyId,
  open,
  onOpenChange,
}: {
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshBudgets();
  const currentYear = Number(todayIstanbul().slice(0, 4));
  const [state, setState] = useState({
    fiscalYear: String(currentYear),
    name: t("budget.defaultName", { year: currentYear }),
    variancePercent: "10",
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/budgets", {
          body: {
            companyId,
            fiscalYear: Number(state.fiscalYear),
            name: state.name.trim(),
            variancePercent: state.variancePercent.trim().replace(",", "."),
            notes: state.notes.trim() || null,
          },
        })
      ).data!,
    onSuccess: async (budget) => {
      onOpenChange(false);
      await refresh();
      toast({ tone: "success", title: t("budget.created") });
      void navigate({ to: "/butce/$id", params: { id: budget.id } });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("budget.new")}
      description={t("budget.newDescription")}
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t("budget.fields.fiscalYear")} required>
            {(control) => (
              <Input
                {...control}
                required
                type="number"
                min={2000}
                max={2999}
                value={state.fiscalYear}
                onChange={(event) => setState({ ...state, fiscalYear: event.target.value })}
              />
            )}
          </FormField>
          <FormField
            label={t("budget.fields.variancePercent")}
            description={t("budget.fields.variancePercentHint")}
          >
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                value={state.variancePercent}
                onChange={(event) => setState({ ...state, variancePercent: event.target.value })}
              />
            )}
          </FormField>
        </div>
        <FormField label={t("budget.fields.name")} required>
          {(control) => (
            <Input
              {...control}
              required
              value={state.name}
              maxLength={150}
              onChange={(event) => setState({ ...state, name: event.target.value })}
            />
          )}
        </FormField>
        <FormField label={t("budget.fields.notes")}>
          {(control) => (
            <Textarea
              {...control}
              value={state.notes}
              maxLength={1000}
              onChange={(event) => setState({ ...state, notes: event.target.value })}
            />
          )}
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={create.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
